import { create } from 'zustand';
import { AI_VISIBILITY_SOURCES } from '../constants/aiVisibility';
import { normalizeMissionContext, type MissionContext } from '../constants/directionInputContext';
import type { AiVisibility, AiVisibilityKey } from '../constants/aiVisibility';

/**
 * A dated Best-Self statement. `reviewedAt` is deliberately separate from
 * `writtenAt`: confirming that a statement still holds is a real act, and it is
 * what clears the "needs attention" state without pretending it was rewritten.
 */
export interface BestSelfStatement {
  statement: string;
  version: number;
  writtenAt: string;
  reviewedAt: string;
  /**
   * ⭐ The North-Star Identity assessment writes these alongside the statement.
   * ALL OPTIONAL, so every record written before the assessment existed stays
   * valid and no migration is needed — the blob simply gains keys.
   *
   * ⚠️ There is exactly ONE identity record in this app. The assessment's
   * "North-Star Identity Statement" and the Growth Journey's "Best-Self
   * statement" are the same artifact under two names; a second store would give
   * two answers to "who am I becoming" that disagree within a week, and Ch19
   * (rewrite from evidence) only works if there is one lineage to compare.
   */
  /** The detailed standards behind the statement. */
  principles?: string[];
  /** What the user intends to stop reinforcing. */
  boundaries?: string[];
  /** The raw picks, so the next version starts from the last one rather than blank. */
  selection?: IdentitySelection;
  /** Where the wording came from — shown so an AI draft is never mistaken for the user's own. */
  source?: 'ai' | 'composed' | 'edited';
}
import { persist } from 'zustand/middleware';
import type { IdentitySelection } from '../utils/northStarIdentity';
import type { IdentityAnalysis } from '../utils/identityGap';
import type { IkigaiMap } from '../constants/ikigai';
import {
  reviseDirection, directionKey,
  type DirectionVersion, type DirectionSource, type DirectionKind,
} from '../utils/directionVersioning';
import { supabase } from '../lib/supabase';
import type { EnergyQuadrant } from '../utils/energyMatrix';
import type { DirectionReport, DirectionReports } from '../constants/directionReport';
import { pushReading, type StoredReading } from '../constants/lifeMap';
import { syncManager } from '../lib/syncManager';
import { dedupeInit, isCloudFresh, markCloudFresh } from '../lib/cloudBoot';
import {
  accountGeneration, accountStorage, cachedUserIdOwner, scopeToAccount,
} from '../lib/accountStorage';
import { changedFields, createDocSync, type DocBase, type DocRules, type DocSync } from '../lib/syncDoc';
import { supabaseBlobTransport } from '../lib/syncBlobTransport';
import type { RefreshOutcome } from '../lib/syncBlob';
import { loadedFor, refreshBlobOnFocus } from '../lib/blobFocusRefresh';

interface VisionAspirations {
  personal: string[];
  professional: string[];
  relationships: string[];
  lifestyle: string[];
  impact: string[];
}

// IKIGAI reflection prompts captured on the Purpose step; saved as identity
// fields AND fed to the AI purpose crafting.
export interface PurposeReflections {
  alive?: string;    // When do you feel most alive?
  unpaid?: string;   // What would you do even if you were never paid?
  problems?: string; // What problems do you feel drawn to solve?
  thanked?: string;  // What do people thank you for?
}

interface UserState {
  name: string;           // display name
  fullName: string;       // legal / full name ('' if unset)
  initials: string;
  avatarUrl: string | null;
  birthDate: string;      // ISO yyyy-mm-dd ('' if unset)
  /**
   * Height in centimetres, or null when not given (Nutrition phase 2: the energy
   * estimate's input; entered in cm or ft/in in Settings → Personal Details).
   * ⚠️ Rides `user_identity.data` — no migration — so it must appear in
   * `DEFAULT_IDENTITY` and `pickIdentity` or it stays local-only.
   */
  heightCm: number | null;
  nationality: string;
  location: string;       // ⚠️ legacy free-text; no longer edited in Settings
  phone: string;          // E.164, e.g. '+966555555555' ('' if unset)
  /**
   * ⚠️ There is NO `lifePurpose`. It existed for a long time — persisted, synced,
   * and read by AIChat and MissionStudio — but nothing in the app ever called its
   * setter, so both readers silently built their prompts with an empty purpose.
   * The real field is `purposeStatement` below (written by UserPurpose, read by
   * everything else).
   *
   * Removed rather than deprecated. ⚠️ The evidence it held no real data is
   * strong but not exhaustive: `setLifePurpose` had no callers, and the key read
   * back as `""` on the only `user_identity` row readable at the time (RLS is
   * read-own, so other accounts could not be checked). If a much older build did
   * populate it, that text was never DISPLAYED anywhere — it only ever fed AI
   * prompt context — so nothing a user could see is lost.
   */
  vision: string;
  purposeEnergizes: string[];
  purposeStrengths: string[];
  purposeMeaningful: string[];
  purposeValue: string[];
  purposeStatement: string;
  /**
   * ⭐ THE PURPOSE VERB — what you are here to DO, the word the statement turns
   * on. Holds either one of `PURPOSE_VERBS`' ids or a verb the user typed;
   * `resolvePurposeVerb` tells them apart. See `constants/purposeVerbs.ts`.
   *
   * ⚠️⚠️ OPTIONAL, AND IT MUST STAY OPTIONAL. Growth Module step 2 completes on
   * "all three statements written"; requiring a verb would change a rule that is
   * already TRUE for existing accounts and un-complete them retroactively.
   *
   * ⚠️ Rides `user_identity.data` — no migration — so it must appear in
   * `DEFAULT_IDENTITY` and `pickIdentity` or it stays local-only.
   */
  purposeVerb: string;
  /**
   * ⭐ THE IKIGAI MAP — the four pillars above, read back as the four regions
   * where they overlap, with `purposeVerb` at the centre. Written on the Ikigai
   * step of the Direction flow; see `constants/ikigai.ts`.
   *
   * ⚠️⚠️ OPTIONAL, LIKE THE VERB, AND FOR THE SAME REASON. Growth Module step 2
   * completes on the three statements; a map that gated anything would change a
   * rule already true for every existing account.
   *
   * ⚠️ Rides `user_identity.data` — no migration — so it must appear in
   * `DEFAULT_IDENTITY` and `pickIdentity` or it stays local-only.
   */
  ikigai: IkigaiMap | null;
  /**
   * ⭐⭐ **THE LONG PURPOSE, AND THE READING BEHIND IT** (D2). The wizard's one AI
   * call returns three things; `purposeStatement` above holds the SHORT one.
   *
   * ⚠️⚠️ **`purposeStatement` STAYS THE CANONICAL FIELD AND STAYS SHORT.** It is
   * what Growth step 2 completes on (`stepProgress.ts`), what `useReveal` treats
   * as a reveal trigger, and what **24 non-test files** read. Making the long
   * form canonical — or renaming anything here — un-completes step 2 for every
   * account that has already finished it. These two are SIBLINGS, never
   * replacements.
   *
   * ⚠️ Ride `user_identity.data` — no migration — so they must appear in
   * `DEFAULT_IDENTITY` and `pickIdentity` or they stay local-only.
   */
  purposeFull: string;
  purposeInsight: string;
  /**
   * ⭐ The same pair for the other two statements (D3). `vision` and
   * `missionStatement` stay the SHORT, canonical fields — these are siblings.
   */
  visionFull: string;
  visionInsight: string;
  missionFull: string;
  missionInsight: string;
  futureAspirations: string[];
  visionAspirations: VisionAspirations;
  /**
   * ⭐ YOUR MISSION — the single, personal one. *How do I move toward the vision?*
   *
   * ⚠️⚠️ DISTINCT FROM `missions` below, which is the seven DOMAIN missions keyed
   * by domainId. Act I ch3 asks for this one; ch5 asks for the domain ones. Until
   * this field existed the chapter's stated output ("Personal Mission V1") had
   * nothing behind it — `missions` is domain-keyed and the Foundation step is
   * literally "your domain missions", so ch3 could not check it without
   * inventing a condition that can never be true.
   *
   * Rides `user_identity.data` — no migration. Versioned under the
   * `'personal-mission'` key; see `directionVersions`.
   */
  missionStatement: string;
  missionContext: MissionContext;
  /** The SEVEN domain missions, keyed by domainId. Not the personal one above. */
  missions: Record<string, string>;
  purposeReflections: PurposeReflections;
  /**
   * Growth Journey Ch3 — the first identity anchor, and Ch19 rewrites it.
   * ⚠️ VERSIONED on purpose: the point of the arc is that who you intend to be
   * changes with evidence, so v1 must survive being superseded. Rides
   * user_identity.data (a jsonb blob), so this needs NO migration.
   * ⚠️ Distinct from `vision`: vision describes the life, this describes the person.
   */
  bestSelf: BestSelfStatement | null;
  /** Superseded versions, newest first. Never overwritten in place. */
  bestSelfHistory: BestSelfStatement[];
  /**
   * ⭐ Version metadata for the CURRENT Purpose / Vision / Mission text, keyed by
   * `directionKey()` ('purpose' · 'vision' · 'mission:<domainId>').
   *
   * ⚠️ The text itself stays in `purposeStatement` / `vision` / `missions` — this
   * map only describes it. Storing a second copy would be a second answer waiting
   * to disagree with the first, and every existing reader would have to choose.
   * The three setters write both halves together, so they cannot drift.
   */
  directionVersions: Record<string, DirectionVersion>;
  /** Superseded direction statements across all three artifacts, newest first. */
  directionHistory: DirectionVersion[];
  /**
   * Stage 1 Steps 2–9: the evidence map, ratings, contradictions, causes, gap
   * and candidates.
   * ⚠️ Rides the SAME jsonb blob as the statement (no migration) and carries
   * `forVersion`, because the analysis belongs to one version of the identity —
   * rewrite the statement and this describes someone you no longer intend to be.
   */
  identityAnalysis: IdentityAnalysis | null;
  /**
   * ⭐ Domain readiness — the ONE state that has to be stored. `domainId → ISO
   * date first introduced`.
   *
   * Everything else on the ladder derives: **Mapped** from a completed Life Map
   * sitting, **Activated** and **Developed** from the domain's quests (see
   * `constants/domainReadiness.ts`). "Has the user met this domain?" is the only
   * question no existing artifact answers, because meeting one leaves no trace.
   *
   * ⚠️ Rides `user_identity.data` — no migration — so it must appear in
   * `DEFAULT_IDENTITY` and `pickIdentity` or it stays local-only.
   */
  domainsDiscovered: Record<string, string>;
  /**
   * ⭐ The user's own claim: `domainId → ISO date they said "I am actively
   * developing this"`. The top rung of the readiness ladder, and the ONLY one
   * the app does not work out for itself (owner call 2026-07-30).
   *
   * ⏳ Stored as a DATE, not a boolean, so a later objective rule can ask how
   * long ago the claim was made and whether anything has happened since.
   *
   * ⚠️ Rides `user_identity.data` — no migration — so it must appear in
   * `DEFAULT_IDENTITY` and `pickIdentity` or it stays local-only.
   */
  domainsDeveloping: Record<string, string>;
  /**
   * ⭐⭐ **ISO date the Life Map was FIRST completed — the durable record of a
   * thing that happened.** `null` until a sitting commits.
   *
   * ⚠️⚠️ **THE ATTEMPT ROW IS EVIDENCE, NOT THE RECORD** (owner, 2026-09-11).
   * `useHasLifeMap()` used to ask `attemptHistory` alone, so clearing assessment
   * history dropped every domain without a Quest from `mapped` back to
   * `discovered` — off the Build page, and Growth Journey step 3 un-completed —
   * for a user who had done all of it. `attemptHistory` is also truncated to the
   * last 50, so it could be lost without anyone deleting a thing.
   *
   * ⚠️ FIRST WRITE WINS, and it is never cleared. Re-taking the map does not
   * move it; the field answers "has this ever happened?", and `attemptHistory`
   * still answers "when, and what did it say?".
   * ⚠️ Rides `user_identity.data` — no migration — so it must appear in
   * `DEFAULT_IDENTITY` and `pickIdentity` or it stays local-only.
   */
  lifeMapCompletedAt: string | null;
  /**
   * ⭐⭐ **THE LATEST DIRECTION REPORT PER KEY** — `directionKey(kind, domainId)`
   * → the full record of what a generation produced (001 phase 2).
   *
   * ⚠️⚠️ **THE SHORT STATEMENTS REMAIN CANONICAL AND UNTOUCHED.** Growth step 2
   * completes on `purposeStatement`, `useReveal` treats it as a trigger, and 24
   * files render it. This sits BESIDE them; nothing here is read to decide
   * whether a statement exists. A report is always absent today — nothing
   * writes one until phase 3 — so every surface must keep working without it.
   *
   * ⚠️ Rides `user_identity.data` — no migration — so it must appear in
   * `DEFAULT_IDENTITY` and `pickIdentity` or it stays local-only. Hydration is
   * `{ ...DEFAULT_IDENTITY, ...row.data }`, so those two are the whole wiring.
   */
  directionReports: DirectionReports;
  /**
   * ⭐⭐ WHEN A LEVEL WAS FIRST REACHED — `"<scope>:<level>" → ISO date`, where
   * scope is `'user'` or a domain id. This is what lets a title card say
   * "Earned 8/1/2026" instead of "Reached at level 10": a title IS its
   * threshold level, so the moment you reached the level is the moment you
   * earned the title, and no separate per-title record is needed.
   *
   * ⚠️⚠️ FORWARD-RECORDING ONLY, deliberately. Nothing in the app has ever
   * stored a level-up date, so levels crossed BEFORE this shipped have none and
   * their cards keep saying "Reached at level N". Backfilling would have to
   * invent a day — which is exactly the bug the old design shipped, where
   * `new Date()` at render made every title claim it was earned today, forever.
   * A missing key is honest; a wrong date is not.
   *
   * ⚠️ First write wins — see `recordLevelReached`. Levels do not get re-earned,
   * and a re-entry must not overwrite the true first date.
   *
   * ⚠️ Rides `user_identity.data` — no migration — so it must appear in
   * `DEFAULT_IDENTITY` and `pickIdentity` or it stays local-only.
   */
  levelReachedAt: Record<string, string>;
  /**
   * ⭐⭐ THE HIGH-WATER MARK OF TOTAL LEVELS — `'user'` and each domain id — and
   * the ONLY thing that decides whether a level-up is celebrated.
   *
   * ⚠️⚠️ IT LIVES IN THE CLOUD ON PURPOSE. The old baseline was localStorage-only,
   * so a device (or a dev server on another port) that had never seen the account
   * had none, defaulted every scope to 0, and read the first real snapshot as a
   * jump from nothing — celebrating levels earned months ago.
   *
   * ⚠️ HIGH-WATER, never a plain "last seen": it only ever moves UP. A snapshot
   * taken before the ~30 stores behind `useUserStats` have hydrated reads as
   * zeros, and storing that would re-arm the very false celebration this exists
   * to stop. A decrease is treated as "not loaded yet" and ignored.
   *
   * ⚠️ `null` = never established. It is established from the first snapshot with
   * real XP, WITHOUT firing — see `useLevelUpDetection`.
   */
  levelBaseline: { user: number; domains: Record<string, number> } | null;
  /**
   * Which energy quadrants open the response window after a journal entry.
   *
   * ⭐ Only the FOUR QUADRANTS are keyed. `getEnergyZone` also returns `Neutral`
   * and `Zoneless` for readings sitting on an axis, and those have no protocols
   * and no toggle — a reading with no quadrant simply never triggers.
   *
   * ⚠️ Rides `user_identity.data` — no migration — so it must appear in
   * `DEFAULT_IDENTITY` and `pickIdentity` or it stays local-only.
   * ⚠️ An ABSENT key means "not set yet", and `zoneTriggerEnabled` reads that
   * as OFF since 2026-09-29 (it was ON) — the window waits for step 18.
   */
  zoneTriggers: Partial<Record<EnergyQuadrant, boolean>>;
  /**
   * Growth Module steps the user has explicitly acknowledged.
   *
   * ⚠️⚠️ This is the ONLY place the module accepts a stored flag instead of an
   * artifact. It is a CLOSED list (`ACK_STEP_IDS`) of steps whose work is
   * *reading or looking at a page* — an act that produces no record. Every other
   * step completes because its output exists. Widening this is how a checklist
   * starts claiming things about a user who did nothing, so each addition is an
   * owner decision, not a convenience.
   *
   * ⚠️ Rides `user_identity.data` — no migration — so it must appear in
   * `DEFAULT_IDENTITY` and `pickIdentity` or it stays local-only.
   */
  moduleAcks: string[];
  /* ⚠️⚠️ `revealEnrolled` WAS HERE and is gone (owner, 2026-08-21). Reveal was
     opt-in and this flag was the opt: `undefined` for every account, so the
     mechanism never applied to anyone. Its only writer was Settings → Developer,
     removed as spent, and rather than give it a new home the owner made reveal
     unconditional — the Growth Journey governs what the app shows.
     ⚠️ A stored `revealEnrolled` may still sit in old identity blobs. It is
     inert: nothing reads it, and `partialize` no longer writes it. */
  /**
   * What the AI assistant is allowed to see about this user.
   *
   * ⚠️⚠️ Stores ONLY the refusals. A key that is absent is *never configured*,
   * which reads as ALLOWED — so a source added in a later release is visible by
   * default and nobody is silently degraded by an update. Turning a source off
   * writes `false`; turning it back on DELETES the key rather than writing
   * `true`. See `constants/aiVisibility.ts`.
   *
   * ⚠️ Rides `user_identity.data` — no migration — so it must appear in
   * `DEFAULT_IDENTITY` and `pickIdentity` or it stays local-only.
   */
  aiVisibility: AiVisibility;
  /**
   * HIGH-WATER set of revealed surfaces. Append-only — reveal never regresses.
   *
   * ⚠️ Needed because step completion is DERIVED: deleting your only quest
   * un-completes the step, and without this the surface it revealed would
   * disappear from the nav. Same high-water idiom as `levelBaseline`.
   */
  revealedSurfaces: string[];
  /** IDs completed in order at least once. Append-only; not current completion. */
  journeyReachedSteps: string[];
  /**
   * ⭐ GROWTH FOUNDATIONS (2026-10-05). Answers to optional setup items the app
   * cannot observe on its own, keyed `<checklistId>.<itemId>` — e.g. the water
   * bottle: `'have'`, `'not-needed'`, or `'acq:<acquisitionId>'` (an item on the
   * Acquisitions list, whose own `acquired` flag then decides "ready").
   */
  foundationChoices: Record<string, string>;
  /**
   * Checklist ids whose REQUIRED items were all done at least once. Append-only,
   * like `journeyReachedSteps`: it is what turns a later gap (a linked habit
   * deleted) into "Needs attention" instead of "In progress".
   */
  foundationsCompleted: string[];
  /**
   * ⭐ PAGE GUIDES (2026-10-10): guide id → the VERSION last finished or skipped
   * on this account. A guide shows by itself only while it is unseen (or rewritten
   * to a higher version). Rides `user_identity.data` — no migration — so it must
   * appear in `DEFAULT_IDENTITY` and `pickIdentity`.
   */
  pageGuidesSeen: Record<string, number>;
  /**
   * ⭐ Life Map AI insights per domain (owner, 2026-10-10), each keyed by the four
   * answers it was written for (`readingKey`), newest first, at most 3. Paid —
   * kept on the account so a reading is never paid for twice.
   */
  lifeMapInsights: Record<string, StoredReading[]>;
  /** Life Map overall AI readings, keyed by the whole map (`mapKey`). */
  lifeMapOverall: StoredReading[];
  /* 🚧 `stepOverrides` WAS HERE — the reveal design-review scaffolding, gone
     2026-08-21 with the epic it was built for. Its only UI was gated on
     `revealEnrolled`, so with the opt-in removed it would have shown to
     everyone. See `GrowthJourneyTrack`. */
  isEditing: boolean;
  initializedUserId: string | null;
  cachedUserId: string | null;
  /**
   * ⭐ The last cloud copy of the identity document this tab agreed with —
   * adopted on load and after every confirmed write. A write sends only what
   * differs from it (see `lib/syncDoc.ts`), which is what stops a stale tab
   * writing its old copy of everything back. `null` = unknown (a cache from
   * before this shipped), read as "nothing here is unsaved".
   * ⚠️ Persisted: without it, an edit whose write had not landed when the tab
   * closed would be indistinguishable from staleness on the next boot.
   */
  syncBase: DocBase<IdentityDoc> | null;
  initialize: () => Promise<void>;
  setName: (name: string) => void;
  setFullName: (fullName: string) => void;
  setAvatarUrl: (url: string | null) => void;
  setBirthDate: (date: string) => void;
  setNationality: (nationality: string) => void;
  setHeightCm: (cm: number | null) => void;
  setLocation: (location: string) => void;
  setPhone: (phone: string) => void;
  /** ⚠️ Versioned — see `directionVersions`. `source` defaults to 'edited'. */
  setVision: (vision: string, source?: DirectionSource) => void;
  setPurposeEnergizes: (items: string[]) => void;
  setPurposeStrengths: (items: string[]) => void;
  setPurposeMeaningful: (items: string[]) => void;
  setPurposeValue: (items: string[]) => void;
  /** ⚠️ Versioned — see `directionVersions`. `source` defaults to 'edited'. */
  setPurposeStatement: (statement: string, source?: DirectionSource) => void;
  setPurposeVerb: (verb: string) => void;
  setIkigai: (map: IkigaiMap | null) => void;
  setPurposeLong: (full: string, insight: string) => void;
  setVisionLong: (full: string, insight: string) => void;
  setMissionLong: (full: string, insight: string) => void;
  setMissionContext: (next: MissionContext) => void;
  setFutureAspirations: (aspirations: string[]) => void;
  addFutureAspiration: (aspiration: string) => void;
  removeFutureAspiration: (index: number) => void;
  setVisionAspirations: (aspirations: VisionAspirations) => void;
  addVisionAspiration: (category: keyof VisionAspirations, aspiration: string) => void;
  removeVisionAspiration: (category: keyof VisionAspirations, index: number) => void;
  /** ⚠️ Versioned — see `directionVersions`. `source` defaults to 'edited'.
   *  This is YOUR mission, the single one — not a domain's. */
  setMissionStatement: (statement: string, source?: DirectionSource) => void;
  /** ⚠️ Versioned — see `directionVersions`. `source` defaults to 'edited'.
   *  One of the SEVEN domain missions. */
  setDomainMission: (domainId: string, mission: string, source?: DirectionSource) => void;
  /**
   * Confirm a direction statement still holds — clears "needs attention" without
   * a rewrite, exactly as `confirmBestSelf` does for the identity statement.
   */
  confirmDirection: (kind: DirectionKind, domainId?: string) => void;
  setPurposeReflection: (key: keyof PurposeReflections, value: string) => void;
  /** Write a new Best-Self statement, archiving the previous one. */
  setBestSelf: (statement: string) => void;
  /** Approve a North-Star Identity — the same record, with its supporting detail. */
  setNorthStar: (input: {
    statement: string;
    principles: string[];
    boundaries: string[];
    selection: IdentitySelection;
    source: 'ai' | 'composed' | 'edited';
  }) => void;
  /** Confirm the current one still holds — clears "needs attention" without a rewrite. */
  confirmBestSelf: () => void;
  /** Read-modify-write the Stage 1 analysis. Pass an updater so concurrent
   *  panels never clobber each other with a stale copy. */
  updateIdentityAnalysis: (fn: (current: IdentityAnalysis | null) => IdentityAnalysis | null) => void;
  /** Record that the user has now met this domain. Idempotent — the FIRST
   *  encounter is the one worth keeping, so a re-visit must not restamp it. */
  markDomainDiscovered: (domainId: string) => void;
  /**
   * Stamp the moment a level was first reached, for `levelReachedAt`.
   * @param scope `'user'` or a domain id
   * @param levels every level newly crossed — a single award can span several
   */
  recordLevelReached: (scope: string, levels: number[]) => void;
  /**
   * Raise the level high-water mark. ⚠️ Only ever raises — see `levelBaseline`.
   * @returns nothing; it is a no-op when nothing would move.
   */
  raiseLevelBaseline: (userTotal: number, domainTotals: Record<string, number>) => void;
  /** Turn the post-entry response window on or off for one quadrant. */
  setZoneTrigger: (quadrant: EnergyQuadrant, enabled: boolean) => void;
  /** Mark a Growth Module step acknowledged. Idempotent; never un-acknowledges. */
  ackModuleStep: (stepId: string) => void;
  /**
   * Allow or refuse one AI context source.
   * ⚠️ Allowing DELETES the key — it never writes `true`, because absent is the
   * value that means "allowed" and two representations of one state drift.
   */
  setAiVisibility: (key: AiVisibilityKey, allowed: boolean) => void;
  /** Set every AI source at once — the onboarding consent answer. */
  setAllAiVisibility: (allowed: boolean) => void;
  /** Union surfaces into the high-water set. ⚠️ NEVER removes. */
  earnRevealSurfaces: (surfaces: readonly string[]) => void;
  /** Union completed-in-order IDs. Callers must wait for identity hydration. */
  earnJourneySteps: (steps: readonly string[]) => void;
  /** Record (or clear, with `null`) an optional setup answer. */
  setFoundationChoice: (key: string, value: string | null) => void;
  /** A page guide was finished or skipped at `version` (never lowers a version). */
  markPageGuideSeen: (id: string, version: number) => void;
  saveLifeMapInsight: (domainId: string, reading: StoredReading) => void;
  saveLifeMapOverall: (reading: StoredReading) => void;
  /** Union checklist ids into the completed-once set. ⚠️ NEVER removes. Callers wait for hydration. */
  earnFoundationsCompleted: (ids: readonly string[]) => void;
  /** Declare (or withdraw) "I am actively developing this domain". */
  setDomainDeveloping: (domainId: string, developing: boolean) => void;
  /** Stamp the first Life Map completion. First write wins; `at` is the real date. */
  markLifeMapCompleted: (at: string) => void;
  /** Store the report a generation produced, under `directionKey(kind, domainId)`. */
  setDirectionReport: (key: string, report: DirectionReport) => void;
  /** Forget one report. Used when its statement is discarded, never on an edit. */
  clearDirectionReport: (key: string) => void;
  setIsEditing: (isEditing: boolean) => void;
}

// ─── Supabase sync (sync epic) ──────────────────────────────────────────────
// Identity + purpose content syncs as ONE row per user (user_identity.data).
// Distinct from profileStore (public profile).
//
// ⚠️⚠️ Writes go through syncDoc's three-way merge, NOT a whole-row upsert. The
// old upsert wrote this tab's copy of EVERY field, so a tab left open since
// yesterday undid whatever another tab or device had done in between the
// moment it changed anything — including automatically (the level baseline,
// page guides, the journey and foundation high-water sets). The worst of it
// was privacy: a stale tab flipped an AI source the user had turned off back to
// allowed. Now a write re-reads the row and applies only what THIS tab changed
// since its `syncBase`; see lib/syncDoc.ts.

const USER_IDENTITY_TABLE = 'user_identity';

const DEFAULT_IDENTITY = {
  name: 'User',
  fullName: '',
  initials: 'U',
  avatarUrl: null as string | null,
  birthDate: '',
  heightCm: null as number | null,
  nationality: '',
  location: '',
  phone: '',
  vision: '',
  purposeEnergizes: [] as string[],
  purposeStrengths: [] as string[],
  purposeMeaningful: [] as string[],
  purposeValue: [] as string[],
  purposeStatement: '',
  purposeVerb: '',
  ikigai: null as IkigaiMap | null,
  purposeFull: '',
  purposeInsight: '',
  visionFull: '',
  visionInsight: '',
  missionFull: '',
  missionInsight: '',
  futureAspirations: [] as string[],
  visionAspirations: {
    personal: [],
    professional: [],
    relationships: [],
    lifestyle: [],
    impact: [],
  } as VisionAspirations,
  // The single personal mission (Foundation step 3)
  missionStatement: '',
  missionContext: {} as MissionContext,
  // domainId → mission line (Foundation step 4, the DOMAIN missions)
  missions: {} as Record<string, string>,
  purposeReflections: {} as PurposeReflections,
  bestSelf: null,
  bestSelfHistory: [],
  identityAnalysis: null as IdentityAnalysis | null,
  directionVersions: {} as Record<string, DirectionVersion>,
  directionHistory: [] as DirectionVersion[],
  // domainId → ISO date the user first met it (the readiness ladder's floor)
  domainsDiscovered: {} as Record<string, string>,
  // domainId → ISO date the user declared they are developing it (the ceiling)
  domainsDeveloping: {} as Record<string, string>,
  // ISO date the Life Map was first completed — durable, survives clearing history
  lifeMapCompletedAt: null as string | null,
  // directionKey → the latest generated report for it. Empty until phase 3 writes one.
  directionReports: {} as DirectionReports,
  // "<scope>:<level>" → ISO date that level was first reached ('user' | domainId)
  levelReachedAt: {} as Record<string, string>,
  // High-water total levels; null until established from a hydrated snapshot.
  levelBaseline: null as { user: number; domains: Record<string, number> } | null,
  // ⚠️ EMPTY, not all-false. An unset key means "never configured" and
  // `zoneTriggerEnabled` reads that as OFF; writing defaults here would make
  // the blob claim the user chose them.
  zoneTriggers: {} as Partial<Record<EnergyQuadrant, boolean>>,
  // Growth Module steps acknowledged (the three that produce no artifact).
  moduleAcks: [] as string[],
  aiVisibility: {} as AiVisibility,
  // ⚠️ NOT `false`. Absent means "never enrolled", which shows everything.
  revealedSurfaces: [] as string[],
  journeyReachedSteps: [] as string[],
  foundationChoices: {} as Record<string, string>,
  foundationsCompleted: [] as string[],
  pageGuidesSeen: {} as Record<string, number>,
  lifeMapInsights: {} as Record<string, StoredReading[]>,
  lifeMapOverall: [] as StoredReading[],
};

function pickIdentity(s: UserState) {
  return {
    name: s.name,
    fullName: s.fullName,
    initials: s.initials,
    avatarUrl: s.avatarUrl,
    birthDate: s.birthDate,
    heightCm: s.heightCm,
    nationality: s.nationality,
    location: s.location,
    phone: s.phone,
    vision: s.vision,
    purposeEnergizes: s.purposeEnergizes,
    purposeStrengths: s.purposeStrengths,
    purposeMeaningful: s.purposeMeaningful,
    purposeValue: s.purposeValue,
    purposeStatement: s.purposeStatement,
    purposeVerb: s.purposeVerb,
    ikigai: s.ikigai,
    purposeFull: s.purposeFull,
    purposeInsight: s.purposeInsight,
    visionFull: s.visionFull,
    visionInsight: s.visionInsight,
    missionFull: s.missionFull,
    missionInsight: s.missionInsight,
    futureAspirations: s.futureAspirations,
    visionAspirations: s.visionAspirations,
    missionStatement: s.missionStatement,
    missionContext: s.missionContext,
    missions: s.missions,
    purposeReflections: s.purposeReflections,
    bestSelf: s.bestSelf,
    bestSelfHistory: s.bestSelfHistory,
    identityAnalysis: s.identityAnalysis,
    // ⚠️ Must be here AND in partialize, or the lineage is local-only and the
    // next device to load the cloud copy silently starts over from v1.
    directionVersions: s.directionVersions,
    directionHistory: s.directionHistory,
    domainsDiscovered: s.domainsDiscovered,
    domainsDeveloping: s.domainsDeveloping,
    lifeMapCompletedAt: s.lifeMapCompletedAt,
    directionReports: s.directionReports,
    levelReachedAt: s.levelReachedAt,
    levelBaseline: s.levelBaseline,
    zoneTriggers: s.zoneTriggers,
    moduleAcks: s.moduleAcks,
    aiVisibility: s.aiVisibility,
    revealedSurfaces: s.revealedSurfaces,
    journeyReachedSteps: s.journeyReachedSteps,
    foundationChoices: s.foundationChoices,
    foundationsCompleted: s.foundationsCompleted,
    pageGuidesSeen: s.pageGuidesSeen,
    lifeMapInsights: s.lifeMapInsights,
    lifeMapOverall: s.lifeMapOverall,
  };
}

type IdentityDoc = ReturnType<typeof pickIdentity>;

/** A cloud blob (or none) as the full document: `{ ...DEFAULT_IDENTITY, ...data }`. */
function hydrateIdentity(data: unknown): IdentityDoc {
  const raw = data && typeof data === 'object' && !Array.isArray(data) ? data : {};
  // ⚠️ The same spread the load always did — present-but-null stays null.
  return pickIdentity({ ...DEFAULT_IDENTITY, ...raw } as unknown as UserState);
}

/** Paid AI readings: newest first, one per key, at most 3 — a reading made on either side is kept. */
function mergeReadings(_base: unknown, local: unknown, cloud: unknown): StoredReading[] {
  const byKey = new Map<string, StoredReading>();
  for (const r of [...(Array.isArray(local) ? local : []), ...(Array.isArray(cloud) ? cloud : [])] as StoredReading[]) {
    const held = byKey.get(r.key);
    if (!held || r.at > held.at) byKey.set(r.key, r);
  }
  return [...byKey.values()].sort((a, b) => b.at.localeCompare(a.at)).slice(0, 3);
}

/** The level high-water mark only ever rises — on both sides at once, take the higher of each. */
function mergeBaseline(_base: unknown, local: unknown, cloud: unknown): UserState['levelBaseline'] {
  const l = local as UserState['levelBaseline'];
  const c = cloud as UserState['levelBaseline'];
  if (!l) return c;
  if (!c) return l;
  const domains: Record<string, number> = { ...c.domains };
  for (const [id, lvl] of Object.entries(l.domains)) domains[id] = Math.max(domains[id] ?? 0, lvl);
  return { user: Math.max(l.user, c.user), domains };
}

/**
 * How each field resolves when this tab AND the cloud both changed it since the
 * tab's base (a field changed on one side only always takes that side). Fields
 * not listed are atomic: this tab's value wins, as the later write.
 *
 * ⚠️ Maps merge KEY BY KEY, so a removal is a removal: allowing an AI source
 * deletes its key, and that delete reaches the cloud even if another tab
 * refused a different source meanwhile — and a tab that touched neither leaves
 * both alone.
 */
const IDENTITY_RULES: DocRules<IdentityDoc> = {
  futureAspirations: 'list',
  visionAspirations: { map: 'list' },
  missions: { map: 'value' },
  purposeReflections: { map: 'value' },
  bestSelfHistory: 'list',
  directionVersions: { map: 'value' },
  directionHistory: 'list',
  domainsDiscovered: { map: 'min' },       // first encounter wins
  domainsDeveloping: { map: 'value' },
  lifeMapCompletedAt: 'min',               // first completion wins
  directionReports: { map: 'value' },
  levelReachedAt: { map: 'min' },          // first reached wins
  levelBaseline: mergeBaseline,
  zoneTriggers: { map: 'value' },
  moduleAcks: 'set',
  aiVisibility: { map: 'value' },
  revealedSurfaces: 'set',
  journeyReachedSteps: 'set',
  foundationChoices: { map: 'value' },
  foundationsCompleted: 'set',
  pageGuidesSeen: { map: 'max' },          // a guide's seen version never lowers
  lifeMapInsights: { map: mergeReadings },
  lifeMapOverall: mergeReadings,
};

const identitySync: DocSync<IdentityDoc> = createDocSync<IdentityDoc>({
  transport: supabaseBlobTransport(USER_IDENTITY_TABLE),
  owner: () => useUserStore.getState().cachedUserId,
  rules: IDENTITY_RULES,
  hydrate: hydrateIdentity,
  read: () => pickIdentity(useUserStore.getState()),
  readBase: () => useUserStore.getState().syncBase,
  adopt: (doc, base) => useUserStore.setState((state: UserState) => ({
    ...changedFields(pickIdentity(state), doc),
    syncBase: base,
  })),
  onWritten: (doc, previousBase, writtenFor) => {
    // The public mirror only needs a write when what it mirrors moved.
    if (previousBase
      && previousBase.name === doc.name
      && previousBase.initials === doc.initials
      && previousBase.avatarUrl === doc.avatarUrl) return;
    void (async () => {
      const { user } = await syncManager.safeAuthCheck();
      // ⚠️ Only for the account this identity was written for: another may have
      // signed in since — it must not take the last one's name and picture.
      if (user && user.id === writtenFor) await mirrorPublicProfile(user.id, doc);
    })();
  },
  onError: (error) => console.error('Failed to sync identity to Supabase:', error),
});

/** Await the pending identity write (tests; an explicit "saved" confirmation). */
export function flushIdentitySync(): Promise<void> {
  return identitySync.flush();
}

/**
 * Catch this tab up with the identity row (lib/blobFocusRefresh.ts): unsaved
 * edits here are kept, everything else becomes the cloud's.
 *
 * ⚠️⚠️ PRIVACY. AI visibility lives here, and the AI chat filters what it sends
 * by THIS TAB's copy — so the chat calls this before every request, not only on
 * focus: a source refused on another device must not leave this one. Unthrottled
 * there; it costs a timestamp unless the row moved, against a call that costs
 * far more.
 */
export function refreshIdentity(): Promise<RefreshOutcome> {
  return identitySync.refresh(loadedFor(() => useUserStore.getState().initializedUserId));
}

refreshBlobOnFocus(USER_IDENTITY_TABLE, refreshIdentity);

// user_profiles is the PUBLIC face other users see (social author chips,
// people search, guild members) — keep its name/initials/avatar in step with
// the identity blob, which is what the app itself edits and displays.
async function mirrorPublicProfile(
  userId: string,
  state: Pick<UserState, 'name' | 'initials' | 'avatarUrl'>
): Promise<void> {
  try {
    const { error } = await supabase.from('user_profiles').upsert(
      {
        id: userId,
        name: state.name,
        initials: state.initials,
        avatar_url: state.avatarUrl || null,
        updated_at: new Date().toISOString(),
      },
      { onConflict: 'id' }
    );
    if (error) console.error('Failed to mirror public profile:', error);
  } catch (error) {
    console.error('Error mirroring public profile:', error);
  }
}

export const useUserStore = create<UserState>()(
  persist<UserState, [], [], any>(
    (rawSet, get) => {
      /**
       * Every setter's `set`. ⚠️ A tab whose load never finished (offline at
       * boot, a failed read) has no base, and with no base a write sends
       * nothing — so the FIRST edit records what the tab held just before it
       * as the base. Everything the tab had came from its cache or the cloud;
       * what it does from here on is its own.
       */
      const set: typeof rawSet = (...args: Parameters<typeof rawSet>) => {
        if (get().syncBase === null) rawSet({ syncBase: pickIdentity(get()) });
        return rawSet(...args);
      };

      // Write this tab's identity edits through to the cloud after a mutation.
      // ⚠️ Coalesced and ONE write in flight at a time (found live 2026-10-09):
      // "Save details" calls three setters in a row, and three concurrent
      // whole-row upserts once raced so the first (taken before the height was
      // set) landed last. syncBlob debounces the burst into one cycle that reads
      // the state when it is built.
      const sync = () => identitySync.schedule();

      /**
       * Apply a direction write: update the text AND its version record together.
       *
       * ⚠️ Both halves move in one `set` so they can never drift. Anything that
       * writes Purpose/Vision/Mission must go through here — a direct
       * `set({ vision })` elsewhere would leave the metadata describing text that
       * is no longer there.
       *
       * ⚠️ An empty write still CLEARS the text (that is the pre-existing
       * behaviour and the UI relies on it) but never touches the lineage.
       * Blanking a field is not a new version, and letting a blank supersede a
       * real V1 would destroy exactly what this feature exists to keep.
       */
      const applyDirection = (
        kind: DirectionKind,
        nextText: string,
        source: DirectionSource | undefined,
        readText: (s: UserState) => string,
        writeText: (s: UserState, text: string) => Partial<UserState>,
        domainId?: string,
      ) => {
        set((state) => {
          const key = directionKey(kind, domainId);
          const result = reviseDirection({
            kind, domainId,
            previousText: readText(state),
            previousVersion: state.directionVersions[key],
            nextText,
            now: new Date().toISOString(),
            source: source ?? 'edited',
          });
          const text = writeText(state, nextText);
          if (!result) return text;
          return {
            ...text,
            directionVersions: { ...state.directionVersions, [key]: result.current },
            directionHistory: result.superseded
              ? [result.superseded, ...state.directionHistory]
              : state.directionHistory,
          };
        });
        sync();
      };

      return {
        ...DEFAULT_IDENTITY,
        isEditing: false,
        initializedUserId: null,
        cachedUserId: null,
        syncBase: null,

        // Load-on-login, keyed by user id (re-runs on account switch). The cloud
        // row wins for every field this tab has not changed since its base; an
        // edit whose write never landed is kept and written. A cache belonging to
        // a different account is replaced outright.
        // ⚠️ Runs on the write chain (`exclusive`) so a write cycle can never
        // interleave with the load and have it adopt an older copy.
        initialize: () => dedupeInit('user_identity', () => identitySync.exclusive(async () => {
          const at = accountGeneration();
          try {
            const { user } = await syncManager.safeAuthCheck();
            if (!user) return;
            if (get().initializedUserId === user.id) return;
            // Cache-first boot: while this user's local cache is fresh, skip the
            // cloud read entirely (write-through keeps cloud current meanwhile).
            if (get().cachedUserId === user.id && isCloudFresh('user_identity', user.id)) {
              rawSet((state) => ({
                initializedUserId: user.id,
                syncBase: state.syncBase ?? pickIdentity(state),
              }));
              return;
            }

            const { data, error } = await supabase
              .from(USER_IDENTITY_TABLE)
              .select('*');
            // An answer for an account that has since signed out is not this copy's.
            if (at !== accountGeneration()) return;
            if (error) throw error;

            const row = (data ?? [])[0];
            const cloudData = row && row.data && typeof row.data === 'object' && Object.keys(row.data).length > 0
              ? row.data
              : null;
            const state = get();
            const cacheIsOurs = state.cachedUserId === null || state.cachedUserId === user.id;
            let unsaved = false;
            if (cacheIsOurs) {
              // With no stored base a cloud row wins outright (what the load
              // always did); with no row at all, everything local is unsaved
              // and gets written (the old load kept it too).
              unsaved = identitySync.reconcile(cloudData);
            } else {
              const cloud = hydrateIdentity(cloudData);
              rawSet({ ...cloud, syncBase: cloud });
            }
            identitySync.noteLoaded(row?.updated_at ?? null);
            rawSet({ initializedUserId: user.id, cachedUserId: user.id });
            markCloudFresh('user_identity', user.id);
            if (unsaved) sync();
            // Self-heal the public mirror: accounts renamed before the mirror
            // existed still carry their signup-era name in user_profiles.
            void mirrorPublicProfile(user.id, get());
          } catch (error) {
            console.error('Failed to initialize identity from Supabase:', error);
          }
        })),

        setName: (name) => {
          set({
            name,
            initials: name.split(' ')
              .map(part => part[0])
              .join('')
              .toUpperCase()
              .slice(0, 2)
          });
          sync();
        },
        setFullName: (fullName) => { set({ fullName }); sync(); },
        setAvatarUrl: (avatarUrl) => { set({ avatarUrl }); sync(); },
        setBirthDate: (birthDate) => { set({ birthDate }); sync(); },
        setNationality: (nationality) => { set({ nationality }); sync(); },
        setHeightCm: (cm) => { set({ heightCm: cm !== null && Number.isFinite(cm) && cm > 0 ? Math.round(cm * 10) / 10 : null }); sync(); },
        setLocation: (location) => { set({ location }); sync(); },
        setPhone: (phone) => { set({ phone }); sync(); },
        setVision: (vision, source) => applyDirection(
          'vision', vision, source,
          (s) => s.vision,
          (_s, text) => ({ vision: text }),
        ),
        setPurposeEnergizes: (purposeEnergizes) => { set({ purposeEnergizes }); sync(); },
        setPurposeStrengths: (purposeStrengths) => { set({ purposeStrengths }); sync(); },
        setPurposeMeaningful: (purposeMeaningful) => { set({ purposeMeaningful }); sync(); },
        setPurposeValue: (purposeValue) => { set({ purposeValue }); sync(); },
        // ⚠️ A plain set + sync, NOT `applyDirection`. That helper is for the
        // three DIRECTION STATEMENTS — it stamps a source and a revision on the
        // record of how a statement came to be written. The verb is an input to
        // a statement, not a statement, and stamping it would put a fourth thing
        // into a history that reads as "you rewrote your purpose".
        setPurposeVerb: (purposeVerb) => { set({ purposeVerb }); sync(); },
        // ⚠️ A plain set + sync, like the verb above and for the same reason: the
        // map READS the three statements, it is not a fourth one, so it must not
        // go through `applyDirection` and land in the rewrite history.
        setIkigai: (ikigai) => { set({ ikigai }); sync(); },
        // ⚠️ ONE setter for both, because they are produced by one AI call and
        // are meaningless apart — an insight describing a statement you no
        // longer hold is worse than no insight. The SHORT statement is written
        // separately, through `setPurposeStatement`, so it still gets its
        // version stamp.
        setPurposeLong: (purposeFull, purposeInsight) => {
          set({ purposeFull, purposeInsight });
          sync();
        },
        setVisionLong: (visionFull, visionInsight) => { set({ visionFull, visionInsight }); sync(); },
        setMissionLong: (missionFull, missionInsight) => { set({ missionFull, missionInsight }); sync(); },
        // Supporting inputs are not a statement revision and do not complete Direction.
        setMissionContext: (next) => { set({ missionContext: normalizeMissionContext(next) }); sync(); },
        setPurposeStatement: (purposeStatement, source) => applyDirection(
          'purpose', purposeStatement, source,
          (s) => s.purposeStatement,
          (_s, text) => ({ purposeStatement: text }),
        ),
        setFutureAspirations: (futureAspirations) => { set({ futureAspirations }); sync(); },
        addFutureAspiration: (aspiration) => {
          set((state) => ({ futureAspirations: [...state.futureAspirations, aspiration] }));
          sync();
        },
        removeFutureAspiration: (index) => {
          set((state) => ({ futureAspirations: state.futureAspirations.filter((_, i) => i !== index) }));
          sync();
        },
        setVisionAspirations: (visionAspirations) => { set({ visionAspirations }); sync(); },
        addVisionAspiration: (category, aspiration) => {
          set((state) => ({
            visionAspirations: {
              ...state.visionAspirations,
              [category]: [...state.visionAspirations[category], aspiration]
            }
          }));
          sync();
        },
        removeVisionAspiration: (category, index) => {
          set((state) => ({
            visionAspirations: {
              ...state.visionAspirations,
              [category]: state.visionAspirations[category].filter((_, i) => i !== index)
            }
          }));
          sync();
        },
        // ⚠️ NO domainId — that is the whole difference from `setDomainMission`
        // below, and it is what keeps this off the `mission:<domainId>` keyspace.
        setMissionStatement: (statement, source) => applyDirection(
          'personal-mission', statement, source,
          (s) => s.missionStatement,
          (_s, text) => ({ missionStatement: text }),
        ),
        setDomainMission: (domainId, mission, source) => applyDirection(
          'mission', mission, source,
          (s) => s.missions[domainId] ?? '',
          (s, text) => ({ missions: { ...s.missions, [domainId]: text } }),
          domainId,
        ),
        confirmDirection: (kind, domainId) => {
          set((state) => {
            const key = directionKey(kind, domainId);
            const current = state.directionVersions[key];
            if (!current) return {};
            return {
              directionVersions: {
                ...state.directionVersions,
                [key]: { ...current, reviewedAt: new Date().toISOString() },
              },
            };
          });
          sync();
        },
        setPurposeReflection: (key, value) => {
          set((state) => ({ purposeReflections: { ...state.purposeReflections, [key]: value } }));
          sync();
        },
        setBestSelf: (statement) => {
          const text = statement.trim();
          if (!text) return;
          set((state) => {
            const now = new Date().toISOString();
            const previous = state.bestSelf;
            // Same text = a confirmation, not a new version. Bumping the version
            // on an unchanged statement would fake growth that didn't happen.
            if (previous && previous.statement === text) {
              return { bestSelf: { ...previous, reviewedAt: now } };
            }
            return {
              bestSelf: { statement: text, version: (previous?.version ?? 0) + 1, writtenAt: now, reviewedAt: now },
              bestSelfHistory: previous ? [previous, ...state.bestSelfHistory] : state.bestSelfHistory,
            };
          });
          sync();
        },
        setNorthStar: ({ statement, principles, boundaries, selection, source }) => {
          const text = statement.trim();
          if (!text) return;
          set((state) => {
            const now = new Date().toISOString();
            const previous = state.bestSelf;
            // ⚠️ Same versioning rule as setBestSelf, and for the same reason:
            // re-approving identical wording is a confirmation. But the
            // supporting detail is still refreshed — the user may have changed
            // which standards they picked without the sentence coming out
            // differently, and dropping that would silently discard real edits.
            if (previous && previous.statement === text) {
              return {
                bestSelf: { ...previous, reviewedAt: now, principles, boundaries, selection, source },
              };
            }
            return {
              bestSelf: {
                statement: text,
                version: (previous?.version ?? 0) + 1,
                writtenAt: now,
                reviewedAt: now,
                principles,
                boundaries,
                selection,
                source,
              },
              bestSelfHistory: previous ? [previous, ...state.bestSelfHistory] : state.bestSelfHistory,
            };
          });
          sync();
        },
        updateIdentityAnalysis: (fn) => {
          set((state) => ({ identityAnalysis: fn(state.identityAnalysis) }));
          sync();
        },
        confirmBestSelf: () => {
          set((state) => (state.bestSelf
            ? { bestSelf: { ...state.bestSelf, reviewedAt: new Date().toISOString() } }
            : {}));
          sync();
        },
        markDomainDiscovered: (domainId) => {
          // ⚠️ The guard is doing two jobs: keeping the stamp on the FIRST
          // encounter, and keeping a re-visit from putting the whole identity
          // blob on the wire — the flow marks on every render pass of a domain
          // screen. `set` is synchronous, so `get()` here is never stale.
          if (get().domainsDiscovered[domainId]) return;
          set((state) => ({
            domainsDiscovered: { ...state.domainsDiscovered, [domainId]: new Date().toISOString() },
          }));
          sync();
        },
        recordLevelReached: (scope, levels) => {
          // ⚠️ FIRST WRITE WINS. A level is reached once; if XP is recomputed or
          // the celebration is re-enqueued, the original date must survive —
          // overwriting would quietly turn a true record into "most recently
          // noticed", which is the failure mode this whole field exists to avoid.
          const existing = get().levelReachedAt;
          const fresh = levels.filter(l => !existing[`${scope}:${l}`]);
          // ⚠️ Nothing new → no `set`, no `sync()`. This runs off a level-up
          // enqueue that can fire repeatedly for the same crossing, and an
          // unconditional write would put the whole identity blob on the wire.
          if (fresh.length === 0) return;
          const at = new Date().toISOString();
          set((state) => ({
            levelReachedAt: {
              ...state.levelReachedAt,
              ...Object.fromEntries(fresh.map(l => [`${scope}:${l}`, at])),
            },
          }));
          sync();
        },
        raiseLevelBaseline: (userTotal, domainTotals) => {
          const prev = get().levelBaseline;
          const next = {
            user: Math.max(prev?.user ?? 0, userTotal),
            domains: { ...(prev?.domains ?? {}) },
          };
          for (const [id, lvl] of Object.entries(domainTotals)) {
            next.domains[id] = Math.max(next.domains[id] ?? 0, lvl);
          }
          // ⚠️ No write when nothing moved. This runs on every stats change, and
          // an unconditional `set` would put the whole identity blob on the wire
          // each time — and defeat the point, since a decrease must be ignored.
          const same = prev
            && prev.user === next.user
            && Object.keys(next.domains).length === Object.keys(prev.domains).length
            && Object.entries(next.domains).every(([k, v]) => prev.domains[k] === v);
          if (same) return;
          set({ levelBaseline: next });
          sync();
        },
        setDirectionReport: (key, report) => {
          // ⚠️ Last write wins, unlike `markLifeMapCompleted`: a report describes
          // the most recent generation, and regenerating is meant to replace it.
          // Lineage of the STATEMENTS lives in `directionVersions`/`directionHistory`.
          set((state) => ({ directionReports: { ...state.directionReports, [key]: report } }));
          sync();
        },
        clearDirectionReport: (key) => {
          // ⚠️ No write when there is nothing to clear — this rides the whole
          // identity blob, and an unconditional sync would put it on the wire
          // for a no-op.
          if (!get().directionReports[key]) return;
          set((state) => {
            const next = { ...state.directionReports };
            delete next[key];
            return { directionReports: next };
          });
          sync();
        },
        markLifeMapCompleted: (at) => {
          // ⚠️ FIRST WRITE WINS, like `recordLevelReached`. The map was first
          // completed once; a retake — or the backfill running on a later load —
          // must not overwrite that with a newer date.
          // ⚠️ Nothing new → no `set`, no `sync()`. The backfill in
          // `useHasLifeMap` runs on every render of every readiness surface, and
          // an unconditional write would put the whole identity blob on the wire
          // each time.
          if (get().lifeMapCompletedAt) return;
          set({ lifeMapCompletedAt: at });
          sync();
        },
        setDomainDeveloping: (domainId, developing) => {
          // ⚠️ Turning it OFF deletes the key rather than storing `false`. The
          // value is the date the claim was made, so a stale date left behind a
          // false would be the wrong answer waiting for the objective rule that
          // is meant to read it later.
          set((state) => {
            const next = { ...state.domainsDeveloping };
            if (developing) next[domainId] = new Date().toISOString();
            else delete next[domainId];
            return { domainsDeveloping: next };
          });
          sync();
        },
        setZoneTrigger: (quadrant, enabled) => {
          // ⚠️ BOTH VALUES ARE WRITTEN EXPLICITLY — unlike `setDomainDeveloping`
          // above. An absent key means "never configured", and what that reads
          // as is a DEFAULT that has already changed once (ON → OFF, 2026-09-29).
          // A stored `false` means the user chose off, whatever the default does.
          set((state) => ({ zoneTriggers: { ...state.zoneTriggers, [quadrant]: enabled } }));
          sync();
        },
        ackModuleStep: (stepId) => {
          // ⚠️ Idempotent and one-way. Re-reading an intro page must not write a
          // second entry, and there is no "un-acknowledge": the user did read it.
          if (get().moduleAcks.includes(stepId)) return;
          set((state) => ({ moduleAcks: [...state.moduleAcks, stepId] }));
          sync();
        },
        setAiVisibility: (key, allowed) => {
          set((state) => {
            const next = { ...state.aiVisibility };
            // ⚠️⚠️ Delete, never `true`. `isSourceVisible` treats absent as
            // allowed, so writing `true` would create a second spelling of one
            // state — and the next source added would have to guess which.
            if (allowed) delete next[key];
            else next[key] = false;
            return { aiVisibility: next };
          });
          sync();
        },
        setAllAiVisibility: (allowed) => {
          /* Answer the AI consent question for every source at once.
             ⚠️ ONE `set` AND ONE `sync`, not a loop over `setAiVisibility`.
             Six calls would be six full upserts of the identity blob on a
             single click during onboarding — and each reads state through its
             own closure, which is how a batch of writes ends up keeping only
             the last one.
             ⚠️ Obeys the same delete-never-true rule, so granting leaves the
             record EMPTY rather than pinning six `true`s: a source added in a
             later release must still default to visible instead of inheriting
             whatever was true the day this ran. */
          set(() => ({
            aiVisibility: allowed
              ? ({} as AiVisibility)
              : (Object.fromEntries(
                  AI_VISIBILITY_SOURCES.map((s) => [s.key, false]),
                ) as AiVisibility),
          }));
          sync();
        },
        earnRevealSurfaces: (surfaces) => {
          // ⚠️ Append-only and idempotent. A surface once revealed stays
          // revealed even if the artifact behind its step is later deleted.
          const held = new Set(get().revealedSurfaces);
          const added = surfaces.filter((s) => !held.has(s));
          if (added.length === 0) return;
          set((state) => ({ revealedSurfaces: [...state.revealedSurfaces, ...added] }));
          sync();
        },
        earnJourneySteps: (steps) => {
          const held = get().journeyReachedSteps;
          const next = [...new Set([...held, ...steps])];
          if (next.length === held.length) return;
          set({ journeyReachedSteps: next });
          sync();
        },
        setFoundationChoice: (key, value) => {
          const held = get().foundationChoices ?? {};
          if ((held[key] ?? null) === value) return;
          const next = { ...held };
          if (value === null) delete next[key]; else next[key] = value;
          set({ foundationChoices: next });
          sync();
        },
        markPageGuideSeen: (id, version) => {
          const held = get().pageGuidesSeen ?? {};
          if ((held[id] ?? 0) >= version) return;
          set({ pageGuidesSeen: { ...held, [id]: version } });
          sync();
        },
        saveLifeMapInsight: (domainId, reading) => {
          const held = get().lifeMapInsights ?? {};
          set({ lifeMapInsights: { ...held, [domainId]: pushReading(held[domainId], reading) } });
          sync();
        },
        saveLifeMapOverall: (reading) => {
          set({ lifeMapOverall: pushReading(get().lifeMapOverall, reading) });
          sync();
        },
        earnFoundationsCompleted: (ids) => {
          const held = get().foundationsCompleted ?? [];
          const next = [...new Set([...held, ...ids])];
          if (next.length === held.length) return;
          set({ foundationsCompleted: next });
          sync();
        },
        // Transient UI flag — not synced.
        setIsEditing: (isEditing) => set({ isEditing }),
      };
    },
    {
      name: 'user-storage',
      // ⭐ One saved copy per account — see `scopeToAccount` below.
      storage: accountStorage(cachedUserIdOwner),
      version: 6,
      // Persist identity + cache owner + sync base; isEditing + initializedUserId stay out.
      partialize: (state) => ({
        ...pickIdentity(state),
        cachedUserId: state.cachedUserId,
        syncBase: state.syncBase,
      } as any),
      migrate: (persistedState: any, version: number) => {
        if (version === 0) {
          return {
            name: persistedState.name || 'User',
            initials: persistedState.initials || 'U',
            avatarUrl: persistedState.avatarUrl || null,
            vision: '',
            purposeEnergizes: [],
            purposeStrengths: [],
            purposeMeaningful: [],
            purposeValue: [],
            purposeStatement: '',
            futureAspirations: [],
            visionAspirations: {
              personal: [],
              professional: [],
              relationships: [],
              lifestyle: [],
              impact: []
            },
            isEditing: false
          };
        }
        if (version === 1 || version === 2) {
          return {
            ...persistedState,
            vision: persistedState.vision || '',
            purposeEnergizes: [],
            purposeStrengths: [],
            purposeMeaningful: [],
            purposeValue: [],
            purposeStatement: '',
            futureAspirations: [],
            visionAspirations: {
              personal: [],
              professional: [],
              relationships: [],
              lifestyle: [],
              impact: []
            },
            isEditing: false
          };
        }
        if (version === 3) {
          return {
            ...persistedState,
            futureAspirations: [],
            missions: {},
            purposeReflections: {}
          };
        }
        if (version === 4 || version === 5) {
          return {
            ...persistedState,
            missions: persistedState.missions || {},
            purposeReflections: persistedState.purposeReflections || {}
          };
        }
        return persistedState as UserState;
      }
    }
  )
);

/* ⚠️⚠️ ONE SAVED COPY PER ACCOUNT (lib/accountStorage). The device kept one copy, tagged with the
   account it was loaded for, and a sign-out does not reload the page: an account's edits whose
   write had not landed lived only until the next account's load replaced the copy, and the next
   account's edits made before its own load were merged into the last one's row by ITS next load. */
scopeToAccount(useUserStore);
